<template>
	<div
		class="event-market-box"
		:class="[`event-market-box-${type}`]"
		:data-event-type="attributes.type"
	>
		<div class="event-market-box-top">
			<div class="event-market-box-top-left">
				<div
					class="event-market-box-icon"
					:class="`icon-${attributes.type}-market-chest-${type}-74`"
				/>
			</div>
			<div class="event-market-box-top-right">
				<div class="event-market-box-title">
					{{ translate(`event.${type}_chest`) }}
				</div>
				<div class="event-market-box-rewards">
					<AppIconButton
						:type="IconTypes.INFO"
						:size="22"
						:iconSize="16"
						@click="openInfo"
					/>
					<div class="event-market-box-rewards-label">
						{{ translate("ui.rewards") }}
					</div>
				</div>
			</div>
		</div>
		<div class="event-market-box-bottom">
			<div class="event-market-box-bottom-title">
				<BidirectionalTextLabel
					:text="translate('ui.open')"
					:theme="textLabelTheme"
					class="rewards-label"
				/>
			</div>

			<div class="event-market-box-bottom-left">
				<div class="event-market-box-bottom-multiplier">1x</div>
				<AppTextButton
					v-if="
						isFreeAvailable &&
						type === attributes.grand_prizes?.dailyFreeTier
					"
					class="event-market-box-bottom-btn"
					:type="StatusTypes.SUCCESS"
					notification="!"
					:text="translate('ui.free')"
					@click="openChest({ multiplier: 1, isFree: true })"
				/>
				<AppTextButton
					v-else
					class="event-market-box-bottom-btn"
					:disabled="
						(attributes.grand_prizes?.chestKeyTier === type &&
							!hasEnoughKeys(1)) ||
						(attributes.grand_prizes?.chestKeyTier !== type &&
							(getTierByType?.costEventium ?? 0) > eventium)
					"
					:type="
						attributes.grand_prizes?.chestKeyTier === type
							? StatusTypes.PRIMARY
							: StatusTypes.WARNING
					"
					:price="{
						type:
							attributes.grand_prizes?.chestKeyTier === type
								? ResourceType.CHEST_KEY
								: ResourceType.EVENTIUM,
						value:
							attributes.grand_prizes?.chestKeyTier === type
								? 1
								: (getTierByType?.costEventium ?? 0),
					}"
					:resourceCheck="
						attributes.grand_prizes?.chestKeyTier !== type
					"
					@click="
						attributes.grand_prizes?.chestKeyTier === type
							? claimForKey(1)
							: openChest({ multiplier: 1 })
					"
				/>
			</div>
			<div class="event-market-box-bottom-right">
				<div class="event-market-box-bottom-multiplier">
					{{ `${CHEST_MULTIPLIER}x` }}
				</div>
				<AppTextButton
					class="event-market-box-bottom-btn"
					:disabled="
						(attributes.grand_prizes?.chestKeyTier === type &&
							!hasEnoughKeys(CHEST_MULTIPLIER)) ||
						(attributes.grand_prizes?.chestKeyTier !== type &&
							(getTierByType?.costEventium ?? 0) *
								CHEST_MULTIPLIER >
								eventium)
					"
					:resourceCheck="
						attributes.grand_prizes?.chestKeyTier !== type
					"
					:type="
						attributes.grand_prizes?.chestKeyTier === type
							? StatusTypes.PRIMARY
							: StatusTypes.WARNING
					"
					:price="{
						type:
							attributes.grand_prizes?.chestKeyTier === type
								? ResourceType.CHEST_KEY
								: ResourceType.EVENTIUM,
						value:
							attributes.grand_prizes?.chestKeyTier === type
								? CHEST_MULTIPLIER
								: (getTierByType?.costEventium ?? 0) *
									CHEST_MULTIPLIER,
					}"
					@click="
						attributes.grand_prizes?.chestKeyTier === type
							? claimForKey(CHEST_MULTIPLIER)
							: openChest({ multiplier: CHEST_MULTIPLIER })
					"
				/>
			</div>
		</div>

		<div class="event-market-box-decoration">
			<GoldenCorner top left />
			<GoldenCorner top right />
			<GoldenCorner bottom left />
			<GoldenCorner bottom right />
			<div class="image-decor" />
		</div>
	</div>
</template>

<script lang="ts" setup>
import { useEventStore } from "@/event";
import { ResourceType } from "@/game/entities/config/resources.ts";
import { getUnlocksByLevel } from "@/game/utils/resources.ts";
import useRewardClaimGuard from "@/hooks/useRewardClaimGuard";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore.ts";
import useResourcesStore from "@/store/useResourcesStore.ts";
import useSettlementsStore from "@/store/useSettlementsStore.ts";
import useUserStore from "@/store/useUserStore.ts";
import { MarketChestType, MarketDropRateType } from "@/types/event";
import type {
	EventGrandPrizeTierConfig,
	EventMilestoneRewardConfig,
} from "@/types/index.ts";
import { IconTypes, LabelThemes, SkinTypes, StatusTypes } from "@/types/ui.ts";
import { floorNumber } from "@/utils/number.ts";
import { translate } from "@/utils/translate.ts";
import { storeToRefs } from "pinia";
import { computed, markRaw, ref } from "vue";
import type { EventMilestoneCurrencyRewardConfig } from "../../types/index.js";
import AppIconButton from "../AppIconButton.vue";
import AppTextButton from "../AppTextButton.vue";
import BidirectionalTextLabel from "../BidirectionalTextLabel.vue";
import GoldenCorner from "../SVG/GoldenCorner.vue";
import EventMarketChestOpened from "./EventMarketChestOpened.vue";

const eventStore = useEventStore();
const { attributes, grandPrizeState } = storeToRefs(eventStore);

const resourcesStore = useResourcesStore();
const { eventium } = storeToRefs(resourcesStore);

const popupStore = usePopupStore();

const userStore = useUserStore();
const { daysAfterRegistration } = storeToRefs(userStore);

const CHEST_MULTIPLIER = 5;

const unlockByLevel = getUnlocksByLevel();

const settlementStore = useSettlementsStore();
const { mainLevel } = storeToRefs(settlementStore);

interface Props {
	type: MarketChestType;
}

export interface OpenedChest {
	type: MarketDropRateType;
	reward: EventMilestoneRewardConfig;
}

const { type } = defineProps<Props>();

const openedChests = ref<Array<Array<OpenedChest>>>([]);

const getTierByType = computed((): EventGrandPrizeTierConfig | undefined => {
	return attributes.value.grand_prizes?.tiers.find(
		(tier: EventGrandPrizeTierConfig): boolean => tier.id === type,
	);
});

const textLabelTheme = computed((): LabelThemes => {
	if (!type) {
		return LabelThemes.MID_GREEN;
	}

	if (type === MarketChestType.COMMON) {
		return LabelThemes.MID_GREEN;
	}
	if (type === MarketChestType.RARE) {
		return LabelThemes.DARK_BLUE;
	}

	if (type === MarketChestType.LEGENDARY) {
		return LabelThemes.PURPLE;
	}

	return LabelThemes.MID_GREEN;
});

const isFreeAvailable = computed((): boolean => {
	if (grandPrizeState.value?.lastDailyFreePickedDaysAfterReg === undefined)
		return false;
	return (
		floorNumber(daysAfterRegistration.value) >
		grandPrizeState.value?.lastDailyFreePickedDaysAfterReg
	);
});

const openInfo = (): void => {
	popupStore.showPopup({
		id: PopupDictionary.EVENT_POSSIBLE_REWARDS,
		type: PopupType.EVENT_POSSIBLE_REWARDS,
		heading: translate("event.possible_rewards"),
		body: type,
		skin: SkinTypes.LIGHT,
		width: 26,
		height: "auto",
		isPaddingless: true,
		isPriority: true,
		closeCallback: handleClaimProcess,
	});
};

const resourcesUnlocks = computed<Partial<Record<ResourceType, boolean>>>(
	(): Partial<Record<ResourceType, boolean>> => {
		const result = {} as Partial<Record<ResourceType, boolean>>;
		(
			Object.entries(unlockByLevel) as Array<[ResourceType, number]>
		).forEach(([key, value]: [ResourceType, number]): void => {
			result[key] = value <= mainLevel.value;
		});
		return result;
	},
);

const generateRewards = (): Array<OpenedChest> | null => {
	const tier = attributes.value.grand_prizes?.tiers.find(
		(tier: EventGrandPrizeTierConfig): boolean => tier.id === type,
	);

	const filteredPools: Record<
		string,
		Array<EventMilestoneCurrencyRewardConfig>
	> = {};
	if (tier) {
		for (const [poolName, items] of Object.entries(tier.pools)) {
			filteredPools[poolName] = items.filter(
				(item: EventMilestoneRewardConfig): boolean => {
					if (item.type === "currency" && item.currency) {
						if (
							resourcesUnlocks.value[
								item.currency as ResourceType
							] === false
						) {
							return false;
						}
					}
					return true;
				},
			);
		}

		const availablePools: Record<
			string,
			Array<EventMilestoneCurrencyRewardConfig>
		> = JSON.parse(JSON.stringify(filteredPools));
		const selectedDrops: Array<OpenedChest> = [];

		let currentGemsClaimed = grandPrizeState.value?.gemsEarnedToday ?? 0;
		let currentKeysClaimed =
			grandPrizeState.value?.chestKeysEarnedToday ?? 0;

		for (let i = 0; i < tier.dropsPerOpen; i++) {
			// Zistíme, ktoré pooly ešte majú dostupné odmeny a spočítame ich celkovú váhu
			const activeWeights: Array<{
				rarity: MarketDropRateType;
				weight: number;
			}> = [];
			let totalWeight = 0;

			for (const [rarity, weight] of Object.entries(tier.poolWeights)) {
				//  Zistíme, ktoré položky v tomto poole sú naozaj dostupné
				const validItems = (availablePools[rarity] || []).filter(
					(item: EventMilestoneCurrencyRewardConfig): boolean => {
						// Ak ide o gemy, skontrolujeme denný limit
						if (
							item.type === "currency" &&
							item.currency === "gems"
						) {
							return (
								!!item.amount &&
								currentGemsClaimed + item.amount <=
									(attributes.value.grand_prizes
										?.dailyGemLimit ?? 30)
							);
						}
						// Ak ide o keys, skontrolujeme denný limit
						if (
							item.type === "currency" &&
							item.currency === "chestKey"
						) {
							return (
								!!item.amount &&
								currentKeysClaimed + item.amount <=
									(attributes.value.grand_prizes
										?.dailyChestKeyLimit ?? 2)
							);
						}
						return true;
					},
				);

				// Ak v poole zostali nejaké platné položky, započítame ho
				if (validItems.length > 0) {
					activeWeights.push({
						rarity: rarity as MarketDropRateType,
						weight,
					});
					totalWeight += weight;
				}
			}

			// Ak už nezostali žiadne dostupné odmeny vo všetkých pooloch, skončíme skôr
			if (totalWeight === 0) break;

			// Náhodne vyberieme raritu (pool) podľa váhy
			let randomRoll = Math.random() * totalWeight;
			let chosenRarity = activeWeights[0].rarity;

			for (const entry of activeWeights) {
				if (randomRoll < entry.weight) {
					chosenRarity = entry.rarity;
					break;
				}
				randomRoll -= entry.weight;
			}

			// Náhodne vyberieme konkrétnu odmenu z vybraného poolu
			const poolItems = availablePools[chosenRarity].filter(
				(item: EventMilestoneCurrencyRewardConfig): boolean => {
					if (item.type === "currency" && item.currency === "gems") {
						return (
							currentGemsClaimed + (item.amount ?? 0) <=
							(attributes.value.grand_prizes?.dailyGemLimit ?? 30)
						);
					}
					if (
						item.type === "currency" &&
						item.currency === "chestKey"
					) {
						return (
							currentKeysClaimed + (item.amount ?? 0) <=
							(attributes.value.grand_prizes
								?.dailyChestKeyLimit ?? 2)
						);
					}
					return true;
				},
			);
			const randomIndex = Math.floor(Math.random() * poolItems.length);

			// Vyberieme item a zároveň ho odstránime z poolu, aby nevznikla duplicita
			const [pickedItem] = poolItems.splice(randomIndex, 1);

			// Odstránime vybranú odmenu z pôvodného poolu (aby sa neopakovala)
			const originalIndex = availablePools[chosenRarity].findIndex(
				(item: EventMilestoneCurrencyRewardConfig): boolean =>
					JSON.stringify(item) === JSON.stringify(pickedItem),
			);
			if (originalIndex !== -1) {
				availablePools[chosenRarity].splice(originalIndex, 1);
			}

			if (
				pickedItem.type === "currency" &&
				pickedItem.currency === "gems"
			) {
				currentGemsClaimed += pickedItem.amount ?? 0;

				const nextGPState = {
					gemsEarnedToday:
						(grandPrizeState.value?.gemsEarnedToday ?? 0) +
						(pickedItem.amount ?? 0),
				};
				eventStore.mergeGrandPrizeState(nextGPState);
			}

			if (
				pickedItem.type === "currency" &&
				pickedItem.currency === "chestKey"
			) {
				currentKeysClaimed += pickedItem.amount ?? 0;

				const nextGPState = {
					chestKeysEarnedToday:
						(grandPrizeState.value?.chestKeysEarnedToday ?? 0) +
						(pickedItem.amount ?? 0),
				};
				eventStore.mergeGrandPrizeState(nextGPState);
			}

			selectedDrops.push({
				type: chosenRarity,
				reward: pickedItem,
			});
		}
		const rarityOrder = [
			MarketDropRateType.COMMON,
			MarketDropRateType.UNCOMMON,
			MarketDropRateType.RARE,
		];

		return selectedDrops.sort((a: OpenedChest, b: OpenedChest): number => {
			return rarityOrder.indexOf(a.type) - rarityOrder.indexOf(b.type);
		});
	}
	return null;
};

const claimForKey = async (multiplier: number): Promise<void> => {
	if (
		grandPrizeState.value &&
		(grandPrizeState.value.chestKeys ?? 0) >= multiplier
	) {
		openChest({ multiplier, isKey: true });
	}
};

const hasEnoughKeys = (multiplier: number): boolean => {
	return (
		(grandPrizeState.value?.chestKeys ?? 0) >= multiplier &&
		type === attributes.value.grand_prizes?.chestKeyTier
	);
};

const handleClaimProcess = (): void => {
	const tabIndex = 1;
	popupStore.updateQueuedPopup(
		{ id: "event" },
		{
			componentProps: { eventTab: tabIndex },
			spriteData: {
				slug: `${attributes.value.type}-event`,
				key: tabIndex + 1,
				size: 416,
				hasOverlap: true,
			},
		},
	);
};

const openChest = ({
	multiplier = 1,
	isKey = false,
	isFree = false,
}: {
	multiplier?: number;
	isKey?: boolean;
	isFree?: boolean;
}): void => {
	if (!useRewardClaimGuard().requireConnection()) return;
	openedChests.value = [];
	for (let i = 0; i < multiplier; i++) {
		openedChests.value.push(generateRewards() ?? []);
	}

	popupStore.showPopup({
		id: PopupDictionary.EVENT_CHEST,
		type: PopupType.INFO,
		isPaddingless: true,
		width: 24.5,
		height: "auto",
		skin: SkinTypes.LIGHT,
		forceDividerLine: true,
		hasOverlap: true,
		hasOuterBar: true,
		isPriority: true,
		componentProps: {
			chestType: type,
			multiplier,
			isKey,
			isFree,
			chests: openedChests.value,
			onClaim: handleClaimProcess,
		},
		component: markRaw(EventMarketChestOpened),
	});
};
</script>

<style lang="scss" scoped>
.event-market-box {
	$connectorHeight: 1rem;
	position: relative;
	align-items: center;
	border-width: 0.05rem;
	border-style: solid;
	border-image-slice: 1;
	padding-top: 0.5rem;
	width: 100%;
	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.6;
		z-index: 0;
		// NOTE: Color-burn blending is better for dark colors
		mix-blend-mode: color-burn;
		background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
			repeat center;
		background-size: 40%;
		width: 100%;
		height: 100%;
		content: "";
	}
	&-top {
		display: flex;
		flex-direction: row;
		justify-content: start;
		align-items: start;
		gap: 0.625rem;
		padding: 0.75rem 0.75rem 1rem 1.25rem;
		&-right {
			display: flex;
			flex-direction: column;
			align-items: start;
			gap: 0.6rem;
			width: 57%;
		}
	}
	&-bottom {
		position: relative;
		background-color: rgba($color: #1f160d, $alpha: 0.4);
		@include flex-row-center;
		&-multiplier {
			color: #fff3dd;
			font-weight: bold;
			font-size: 0.906rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}
		&-btn {
			width: 90%;
			:deep(.price) {
				margin: 0;
			}
		}
		&-left,
		&-right {
			padding: 0.75rem;
			width: 50%;
			@include flex-col-center;
			gap: 0.4rem;
		}
		&-left {
			border-right-width: 0.063rem;
			border-right-style: solid;
			border-image-source: linear-gradient(
				0deg,
				rgba(192, 139, 38, 1) 0%,
				rgba(206, 174, 86, 1) 100%
			);
			border-image-slice: 1;
		}
		&-title {
			position: absolute;
			top: -0.9rem;
			left: 50%;
			transform: translateX(-50%);
		}
	}
	&-decoration {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 1;
		width: 100%;
		height: 100%;
		pointer-events: none;

		:deep(.golden-corner) {
			width: 1.5rem;
			height: 1.5rem;
		}

		:deep(.golden-corner--top-left) {
			position: absolute;
			top: 0;
			left: 0;
		}

		:deep(.golden-corner--top-right) {
			position: absolute;
			top: 0;
			right: 0;
			z-index: 1;
		}

		:deep(.golden-corner--bottom-left) {
			position: absolute;
			bottom: 0;
			left: 0;
		}

		:deep(.golden-corner--bottom-right) {
			position: absolute;
			right: 0;
			bottom: 0;
		}
		.image-decor {
			position: absolute;
			top: 0.05rem;
			right: 0.05rem;
			width: 5rem;
			height: 5rem;
		}
	}
	&-title {
		font-weight: bold;
		font-size: 1.25rem;
		line-height: 1.3rem;
		text-transform: uppercase;
	}
	&-rewards {
		@include flex-row-center;
		gap: 0.25rem;
		&-label {
			color: #fff3dd;
			font-size: 0.906rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}
	}
	&[data-event-type="solarfair"] {
		.image-decor {
			@include background(
				url($path-events + "solarfair/backgrounds/sun-corner.avif"),
				contain,
				center,
				no-repeat
			);
		}
		&.event-market-box {
			border-image-source: linear-gradient(
				0deg,
				rgba(192, 139, 38, 1) 0%,
				rgba(206, 174, 86, 1) 100%
			);
			&-common {
				background-image: linear-gradient(
					0deg,
					rgba(171, 136, 73, 1) 0%,
					rgba(242, 220, 181, 1) 100%
				);
				.event-market-box-title {
					color: #372615;
				}
				.event-market-box-rewards-label {
					color: #372615;
				}
			}
			&-rare {
				background-image: linear-gradient(
					0deg,
					rgba(9, 35, 77, 1) 0%,
					rgba(54, 92, 153, 1) 100%
				);
				.event-market-box-title {
					color: #f4d278;
				}
			}
			&-legendary {
				background-image: linear-gradient(
					0deg,
					rgba(61, 9, 77, 1) 0%,
					rgba(130, 54, 153, 1) 100%
				);
				.event-market-box-title {
					color: #f4d278;
				}
			}
		}
	}
	&[data-event-type="pirates"] {
		.image-decor {
			@include background(
				url($path-events + "pirates/backgrounds/skull-corner.avif"),
				contain,
				center,
				no-repeat
			);
		}
		.event-market-box-top-right {
			width: 65%;
		}
		&.event-market-box {
			border-image-source: linear-gradient(
				0deg,
				rgba(192, 139, 38, 1) 0%,
				rgba(206, 174, 86, 1) 100%
			);
			&-common {
				background-image: linear-gradient(
					0deg,
					rgba(171, 136, 73, 1) 0%,
					rgba(242, 220, 181, 1) 100%
				);
				.event-market-box-title {
					color: #372615;
				}
				.event-market-box-rewards-label {
					color: #372615;
				}
			}
			&-rare {
				background-image: linear-gradient(
					0deg,
					rgba(9, 35, 77, 1) 0%,
					rgba(54, 92, 153, 1) 100%
				);
				.event-market-box-title {
					color: #f4d278;
				}
			}
			&-legendary {
				background-image: linear-gradient(
					0deg,
					rgba(61, 9, 77, 1) 0%,
					rgba(130, 54, 153, 1) 100%
				);
				.event-market-box-title {
					color: #f4d278;
				}
			}
		}
	}
}
</style>
